import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, Clock, BookOpen } from 'lucide-react';
import { useContent } from '../context/ContentContext';
import ShareBar from '../components/ShareBar';
import ReadingProgress from '../components/ReadingProgress';

export default function ArticleDetail() {
  const { articles, authoredArticles } = useContent();
  const { slug } = useParams();
  const navigate = useNavigate();
  const pool = [...(authoredArticles || []), ...(articles || [])];
  const article = pool.find((a) => a.slug === slug);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!article) {
    return (
      <div className="section-pad pt-40 pb-32 text-center">
        <h1 className="font-display text-4xl">Story not found</h1>
        <Link to="/magazine" className="inline-flex items-center gap-1.5 mt-6 text-[#111111] font-medium">
          <ArrowLeft size={17} /> Back to magazine
        </Link>
      </div>
    );
  }

  const body = (article.body && article.body.length) ? article.body : (article.excerpt ? [article.excerpt] : []);
  const suggestions = pool.filter((a) => a.slug !== article.slug && a.title).slice(0, 3);

  return (
    <article>
      <ReadingProgress />
      <div className="section-pad pt-32 md:pt-36 pb-10">
        <div className="max-w-3xl mx-auto">
          <button onClick={() => navigate(-1)} data-testid="article-back" className="inline-flex items-center gap-1.5 text-[#666666] hover:text-[#111111] transition-colors mb-8">
            <ArrowLeft size={17} /> Back
          </button>
          {article.category ? <p className="eyebrow text-[#D01919] mb-4">{article.category}</p> : null}
          <h1 className="font-display text-4xl md:text-6xl leading-[1.04]" data-testid="article-title">{article.title}</h1>
          {article.excerpt ? <p className="text-[#666666] text-xl mt-6 leading-relaxed">{article.excerpt}</p> : null}
          <div className="flex items-center gap-4 mt-8 pb-8 border-b border-[#e5e5e5]">
            {article.author ? (
              <>
                <div className="w-11 h-11 rounded-full bg-[#111111] text-[#D01919] flex items-center justify-center font-display text-lg">
                  {article.author.charAt(0)}
                </div>
                <div>
                  <p className="font-medium">{article.author}</p>
                  {article.role ? <p className="text-sm text-[#666666]">{article.role}</p> : null}
                </div>
              </>
            ) : null}
            {article.readTime ? (
              <div className="ml-auto flex items-center gap-2 text-sm text-[#666666]"><Clock size={15} /> {article.readTime}</div>
            ) : null}
          </div>
          <ShareBar url={`${window.location.origin}/api/share/article/${article.slug}`} text={`${article.title} — ENERSIDER`} testid="article-share" />
        </div>
      </div>


      <div className="section-pad py-14">
        <div className="max-w-3xl mx-auto">
          {body.map((p, i) => (p.startsWith('Q: ') ? (
            <p key={i} className="mt-10 font-display text-xl md:text-2xl leading-snug text-[#D01919]" data-testid="qa-question">{p.slice(3)}</p>
          ) : (
            <p key={i} className={`text-[1.18rem] leading-[1.8] text-[#1a1a1a] ${i === 0 ? 'first-letter:font-display first-letter:text-6xl first-letter:float-left first-letter:mr-3 first-letter:leading-[0.8] first-letter:text-[#111111]' : 'mt-6'}`}>
              {p}
            </p>
          )))}

          <div className="mt-12 pt-8 border-t border-[#e5e5e5]"><p className="eyebrow text-[#666666]">Share this article</p><ShareBar url={`${window.location.origin}/api/share/article/${article.slug}`} text={`${article.title} — ENERSIDER`} testid="article-share-bottom" className="mt-4" /></div>
          {article.sourceIssue ? (
            <div className="mt-12 p-8 bg-[#111111] text-white rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5" data-testid="article-issue-cta">
              <div>
                <p className="font-display text-2xl">Read the full article</p>
                <p className="text-white/70 mt-1">This piece appears in the {article.sourceIssue} edition of the Enersider magazine.</p>
              </div>
              <Link to="/editions" className="inline-flex items-center gap-1.5 bg-white text-[#111111] font-medium px-6 py-3.5 rounded-full hover:bg-[#D01919] hover:text-white transition-colors shrink-0">
                <BookOpen size={18} /> Open the issue
              </Link>
            </div>
          ) : (
            <div className="mt-12 p-8 bg-[#f4f4f4] rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
              <div>
                <p className="font-display text-2xl">Enjoyed this analysis?</p>
                <p className="text-[#666666] mt-1">Get the monthly edition in your inbox.</p>
              </div>
              <Link to="/contact" className="inline-flex items-center gap-1.5 bg-[#111111] text-[#ffffff] font-medium px-6 py-3.5 rounded-full hover:bg-[#D01919] transition-colors shrink-0">
                Subscribe <ArrowUpRight size={18} />
              </Link>
            </div>
          )}
        </div>
      </div>

      {suggestions.length > 0 && (
        <div className="section-pad pb-24">
          <div className="max-w-[1500px] mx-auto">
            <h2 className="font-display text-3xl md:text-4xl mb-8">Keep reading</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {suggestions.map((a) => (
                <Link key={a.slug} to={`/article/${a.slug}`} className="group flex flex-col border-t-2 border-[#111111] pt-5 hover:border-[#D01919] transition-colors" data-testid="article-suggestion">
                  {a.category ? <p className="eyebrow text-[#D01919] mb-2">{a.category}</p> : null}
                  <h3 className="font-display text-xl leading-snug group-hover:text-[#D01919] transition-colors">{a.title}</h3>
                  {a.excerpt ? <p className="text-[#666666] text-sm mt-2 line-clamp-3">{a.excerpt}</p> : null}
                  {a.author ? <p className="text-xs text-[#666666] mt-3">By {a.author}</p> : null}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </article>
  );
}
